import type { SupportCaseDetail } from "@wallyo/contracts/console/cases";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { getSupportCase, listSupportCases } from "../../api/console-api";
import { useApi } from "../../api/context";
import { ConsoleApiError } from "../../api/errors";
import { createIntent } from "../../api/idempotency";
import { t } from "../../i18n";
import { useHash } from "../../navigation/use-hash";
import { useAlerts } from "../../shell/alerts";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { canOpenCase, canReplyInApp, caseControls } from "./case-access";
import { CloseDialog, LinkDialog, NewCallDialog, ReassignDialog } from "./CaseDialogs";
import { CasePanelView, type CaseDialogKind, type ComposerEvent, type ComposerState } from "./CasePanelView";
import { CaseQueueView } from "./CaseQueueView";
import { casesHref, parseCasesHash, queueRequest, type QueueFilters } from "./case-route";
import {
  bodyProblem,
  claimWinner,
  describeCaseFailure,
  minutesProblem,
  sendCall,
  sendClaim,
  sendNote,
  sendReply,
  type CaseFailure,
} from "./case-writes";
import { INITIAL_QUEUE_STATE, queueReducer } from "./queue-state";
import { useNow } from "./use-now";

/** The deadline pills count down on this beat. */
const TICK_MS = 15_000;

/**
 * Dossiers: the shared queue (GET /console/cases) beside the open case
 * (`#cases/<id>`, GET /console/cases/:id). Every write names the version on
 * screen with one Idempotency-Key per intent, and the screen changes only
 * from the server's answer (ADR-0016). After each write the queue is read
 * again, so owners and deadlines stay true.
 */
export function CasesScreen() {
  const hash = useHash();
  const { me } = useStaff();
  const { client } = useApi();
  const route = parseCasesHash(hash);
  const { filters, supportCaseId } = route;
  const now = useNow(TICK_MS);
  const [queue, dispatch] = useReducer(queueReducer, INITIAL_QUEUE_STATE);
  const [queueReads, readQueue] = useReducer((count: number) => count + 1, 0);
  const [newCall, setNewCall] = useState(false);
  const generation = useRef(0);
  const shownFilters = useRef<string | null>(null);
  const filtersKey = casesHref(filters);

  useEffect(() => {
    const mine = ++generation.current;
    // The same filters read again keep their rows on screen until the answer arrives.
    dispatch({ type: "load", keep: shownFilters.current === filtersKey });
    shownFilters.current = filtersKey;
    listSupportCases(client, queueRequest(parseCasesHash(filtersKey).filters)).then(
      (page) => generation.current === mine && dispatch({ type: "loaded", page }),
      (error: unknown) => generation.current === mine && dispatch({ type: "failed", error }),
    );
  }, [client, filtersKey, queueReads]);

  const loadMore = useCallback(() => {
    if (!queue.nextCursor || queue.loadingMore) return;
    const mine = generation.current;
    dispatch({ type: "loadMore" });
    listSupportCases(client, queueRequest(parseCasesHash(filtersKey).filters, queue.nextCursor)).then(
      (page) => generation.current === mine && dispatch({ type: "loadedMore", page }),
      (error: unknown) => generation.current === mine && dispatch({ type: "moreFailed", error }),
    );
  }, [client, filtersKey, queue.nextCursor, queue.loadingMore]);

  const onFilters = useCallback(
    (next: QueueFilters) => {
      window.location.hash = casesHref(next, supportCaseId);
    },
    [supportCaseId],
  );

  return (
    <>
      <PageTop title={t("screen.cases")} sub={t("sub.cases")} />
      <div className="split list cases">
        <CaseQueueView
          filters={filters}
          state={queue}
          now={now}
          selectedId={supportCaseId}
          canOpenCase={canOpenCase(me)}
          onFilters={onFilters}
          onRetry={readQueue}
          onLoadMore={loadMore}
          onNewCall={() => setNewCall(true)}
        />
        {supportCaseId ? (
          // Keyed by the case: another case starts from nothing, drafts included.
          <CasePanel key={supportCaseId} supportCaseId={supportCaseId} now={now} onChanged={readQueue} />
        ) : (
          <section className="card case-panel">
            <div className="empty">{t("cases.pick")}</div>
          </section>
        )}
      </div>
      {newCall ? (
        <NewCallDialog
          canSearch={me.features.includes("SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT")}
          onClose={() => setNewCall(false)}
          onOpened={(detail) => {
            setNewCall(false);
            readQueue();
            window.location.hash = casesHref(filters, detail.supportCaseId);
          }}
        />
      ) : null}
    </>
  );
}

type PanelState =
  | { status: "loading"; detail: SupportCaseDetail | null }
  | { status: "ready"; detail: SupportCaseDetail }
  | { status: "failed"; detail: SupportCaseDetail | null; error: unknown };

const EMPTY_COMPOSER: ComposerState = {
  mode: "reply",
  body: "",
  minutes: "",
  problem: null,
  failure: null,
  pending: false,
};

function CasePanel({ supportCaseId, now, onChanged }: { supportCaseId: string; now: Date; onChanged: () => void }) {
  const { client } = useApi();
  const { me } = useStaff();
  const myId = me.staffMember.staffMemberId;
  const [state, setState] = useState<PanelState>({ status: "loading", detail: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [composer, setComposer] = useState<ComposerState>(EMPTY_COMPOSER);
  const [claiming, setClaiming] = useState(false);
  const [claimFailure, setClaimFailure] = useState<CaseFailure | null>(null);
  const [dialog, setDialog] = useState<CaseDialogKind | null>(null);
  const alerts = useAlerts();
  const composerIntent = useRef(createIntent());
  const claimIntent = useRef(createIntent());

  useEffect(() => {
    let current = true;
    setState((was) => ({ status: "loading", detail: was.detail }));
    getSupportCase(client, supportCaseId).then(
      ({ supportCase }) => current && setState({ status: "ready", detail: supportCase }),
      (error: unknown) => current && setState((was) => ({ status: "failed", detail: was.detail, error })),
    );
    return () => {
      current = false;
    };
  }, [client, supportCaseId, reads]);

  /** The case as the server answered a write: shown, and the queue read again. */
  const apply = useCallback(
    (detail: SupportCaseDetail, message: string) => {
      setState({ status: "ready", detail });
      alerts.ok(message);
      onChanged();
    },
    [alerts, onChanged],
  );

  /** Read the case again after it changed meanwhile. Drafts stay; a new version is a new intent. */
  const reload = useCallback(() => {
    composerIntent.current.reset();
    claimIntent.current.reset();
    setClaimFailure(null);
    setDialog(null);
    setComposer((current) => ({ ...current, failure: null, problem: null }));
    read();
    onChanged();
  }, [onChanged]);

  const detail = state.detail;

  if (!detail) {
    return (
      <section className="card case-panel">
        {state.status === "failed" ? (
          <div className="audit-error">
            <p className="foot">
              {state.error instanceof ConsoleApiError && state.error.status === 404
                ? t("cases.notFound")
                : t("cases.loadFailed")}
            </p>
            <ErrorNotice error={state.error} />
            <div>
              <button className="btn primary" type="button" onClick={read}>
                {t("cases.retry")}
              </button>
            </div>
          </div>
        ) : (
          <div className="empty" role="status" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            <span>{t("cases.loading")}</span>
          </div>
        )}
      </section>
    );
  }

  const controls = caseControls(me, detail);
  const replyInApp = canReplyInApp(detail);
  const shownComposer: ComposerState = {
    ...composer,
    mode: composer.mode === "reply" && !controls.reply ? "note" : composer.mode,
  };

  const claim = async () => {
    if (claiming) return;
    setClaiming(true);
    setClaimFailure(null);
    try {
      const { supportCase } = await sendClaim(client, detail, claimIntent.current.key());
      claimIntent.current.reset();
      setComposer((current) => ({ ...current, mode: "reply" }));
      apply(supportCase, t("cases.claim.done", { reference: supportCase.reference }));
    } catch (error) {
      // Read the case again: if someone else won the race, say who.
      let current: SupportCaseDetail | null = null;
      if (error instanceof ConsoleApiError && error.code !== "stale_version") {
        current = await getSupportCase(client, supportCaseId).then(
          ({ supportCase }) => supportCase,
          () => null,
        );
      }
      if (current) {
        claimIntent.current.reset();
        setState({ status: "ready", detail: current });
        onChanged();
      }
      if (current?.owner?.staffMemberId === myId) {
        // The answer was lost, but the claim was ours.
        alerts.ok(t("cases.claim.done", { reference: current.reference }));
      } else {
        setClaimFailure(describeCaseFailure(error, claimWinner(error, current, myId)));
      }
    } finally {
      setClaiming(false);
    }
  };

  const submitComposer = async () => {
    if (shownComposer.pending) return;
    const { mode, body, minutes } = shownComposer;
    const problem =
      mode === "reply"
        ? replyInApp
          ? bodyProblem(body)
          : t("cases.reply.noChannel")
        : mode === "call"
          ? (bodyProblem(body) ?? minutesProblem(minutes))
          : bodyProblem(body);
    setComposer((current) => ({ ...current, problem, failure: null }));
    if (problem) return;
    setComposer((current) => ({ ...current, pending: true }));
    const key = composerIntent.current.key();
    try {
      const { supportCase } =
        mode === "reply"
          ? await sendReply(client, detail, body, key)
          : mode === "call"
            ? await sendCall(client, detail, { body, minutes }, key)
            : await sendNote(client, detail, body, key);
      composerIntent.current.reset();
      setComposer((current) => ({ ...current, body: "", minutes: "", pending: false }));
      apply(
        supportCase,
        mode === "reply" ? t("cases.reply.doneChat") : mode === "call" ? t("cases.call.done") : t("cases.note.done"),
      );
    } catch (error) {
      setComposer((current) => ({ ...current, pending: false, failure: describeCaseFailure(error) }));
    }
  };

  const onComposer = (event: ComposerEvent) => {
    if (event.type === "submit") {
      void submitComposer();
      return;
    }
    // Anything edited is a new intent, and the old problem no longer applies.
    composerIntent.current.reset();
    setComposer((current) => {
      const next = { ...current, problem: null };
      switch (event.type) {
        case "mode":
          return { ...next, mode: event.mode, failure: null };
        case "body":
          return { ...next, body: event.body };
        case "minutes":
          return { ...next, minutes: event.minutes };
      }
    });
  };

  const dialogProps = {
    detail,
    onClose: () => setDialog(null),
    onReload: reload,
    onDone: (next: SupportCaseDetail, message: string) => {
      setDialog(null);
      apply(next, message);
    },
  };

  return (
    <div className="case-column">
      {state.status === "failed" ? (
        <div className="banner bad" role="alert">
          <span>{t("cases.refreshFailed")}</span>
          <button className="btn" type="button" onClick={read}>
            {t("cases.retry")}
          </button>
        </div>
      ) : null}
      <CasePanelView
        detail={detail}
        controls={controls}
        canReplyInApp={replyInApp}
        composer={shownComposer}
        now={now}
        claiming={claiming}
        claimFailure={claimFailure}
        onClaim={() => void claim()}
        onDialog={setDialog}
        onComposer={onComposer}
        onReload={reload}
      />
      {dialog === "reassign" ? <ReassignDialog {...dialogProps} staffPicker={controls.staffPicker} /> : null}
      {dialog === "close" ? <CloseDialog {...dialogProps} /> : null}
      {dialog === "link" ? <LinkDialog {...dialogProps} canSearch={controls.customerSearch} /> : null}
    </div>
  );
}
